import {
	useQuery,
	useInfiniteQuery,
	useQueryClient,
	useMutation,
} from "react-query";
import { toast } from "react-toastify";
import API from "./utils/api";
import {
	PayPeriodDto,
	PayPeriodPaginator,
	CreatePayPeriodDto,
	UpdatePayPeriodDto,
	QueryPayPeriodDto,
	SearchPayPeriodDto,
	AutoCreatePayPeriodDto,
} from "../models/pay-period.dto";
import ENDPOINTS from "./utils/endpoints";
import { QueryOptionsDto } from "../models/generic.dto";

export function useGetPayPeriod(options?: QueryPayPeriodDto) {
	const url = ENDPOINTS.PROCESSING_COMPANY.PAY_PERIOD.GET;
	const request = (options: QueryPayPeriodDto) =>
		API.ProcessingCompanyAPI.getPayPeriod(options);

	const { data, isLoading, error, refetch } = useQuery<
		PayPeriodPaginator,
		Error
	>(
		[url, options],
		({ queryKey, pageParam }) =>
			request(Object.assign({}, queryKey[1], pageParam)),
		{ ...API.Handlers.pageParamOption },
	);

	return { data, isLoading, error, refetch };
}

export function useGetPayPeriodSearch() {
	const queryClient = useQueryClient();

	return useMutation(
		(options?: SearchPayPeriodDto) =>
			API.ProcessingCompanyAPI.getPayPeriodSearch(options),
		{
			onSuccess: (response) => {},
			onError: API.Handlers.onErrorHandler,
			onSettled: () => {
				queryClient.invalidateQueries(ENDPOINTS.HR.LEAVE_PACKAGE.GET);
			},
		},
	);
}

export function useGetPayPeriodById(id: number) {
	const url = ENDPOINTS.PROCESSING_COMPANY.PAY_PERIOD.DETAIL;
	const request = API.ProcessingCompanyAPI.getPayPeriodById(id);

	const { data, isLoading, error } = useQuery<{ data: PayPeriodDto }, Error>(
		[url, id],
		() => request,
		{
			enabled: !!id,
		},
	);

	return {
		data: data,
		isLoading,
		error,
	};
}
export function useCreatePayPeriod() {
	const queryClient = useQueryClient();

	return useMutation(
		({ payload }: { payload: CreatePayPeriodDto }) =>
			API.ProcessingCompanyAPI.createPayPeriod(payload),
		{
			onSuccess: () => {
				toast.success("Record created successfully");
				queryClient.invalidateQueries(
					ENDPOINTS.PROCESSING_COMPANY.PAY_PERIOD.GET,
				);
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useAutoCreatePayPeriod() {
	const queryClient = useQueryClient();

	return useMutation(
		({ payload }: { payload: AutoCreatePayPeriodDto }) =>
			API.ProcessingCompanyAPI.autoCreatePayPeriod(payload),
		{
			onSuccess: () => {
				toast.success("Record created successfully");
				queryClient.invalidateQueries(
					ENDPOINTS.PROCESSING_COMPANY.PAY_PERIOD.GET,
				);
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useUpdatePayPeriod() {
	const queryClient = useQueryClient();

	return useMutation(
		({ payload, id }: { payload: UpdatePayPeriodDto; id: number }) =>
			API.ProcessingCompanyAPI.updatePayPeriod(id, payload),
		{
			onSuccess: () => {
				toast.success("Record updated successfully");
				queryClient.invalidateQueries(
					ENDPOINTS.PROCESSING_COMPANY.PAY_PERIOD.GET,
				);
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useDeletePayPeriod() {
	return useMutation(
		({ id }: { id: number }) => API.ProcessingCompanyAPI.deletePayPeriod(id),
		{
			onSuccess: () => {
				toast.success("Record deleted successfully");
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useClosePayPeriod() {
	const queryClient = useQueryClient();

	return useMutation(
		({ id }: { id: number }) => API.ProcessingCompanyAPI.closePayPeriod(id),
		{
			onSuccess: () => {
				toast.success("Pay period closed successfully");
				queryClient.invalidateQueries(
					ENDPOINTS.PROCESSING_COMPANY.PAY_PERIOD.GET,
				);
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}
